/*
 * DateSelectionReducer
 *
 * The reducer takes care of our data. Using actions, we can change our application state.
 * To add a new action, add it to the ACTION_HANDLERS object.
 */
import { ROOM_TYPES, PROPERTY_CODES, PROPERTY_WETUMPKA } from 'utils/constants';
import moment from 'moment';
import { getDefaultPropertyForTheme } from 'utils/themeUtils';
import { isMaxStayExceeded } from 'utils/dateUtils';

// Actions
const SET_START_DATE = 'app/DateSelection/SET_START_DATE';
const SET_END_DATE = 'app/DateSelection/SET_END_DATE';
const SET_PROPERTY = 'app/DateSelection/SET_PROPERTY';
const SET_NUMBER_OF_GUESTS = 'app/DateSelection/SET_NUMBER_OF_GUESTS';
const SET_QUERY_INPUT = 'app/DateSelection/SET_QUERY_INPUT';
const SET_ACCOUNT_NUMBER = 'app/DateSelection/SET_ACCOUNT_NUMBER';
const SET_SECOND_ACCOUNT_NUMBER = 'app/DateSelection/SET_SECOND_ACCOUNT_NUMBER';
const SET_ACCOUNT_PIN = 'app/DateSelection/SET_ACCOUNT_PIN';
const SUBMIT_REWARDS_ACCOUNT = 'app/DateSelection/SUBMIT_REWARDS_ACCOUNT';
const SET_BLOCK_CODE = 'app/DateSelection/SET_BLOCK_CODE';
const SUBMIT_BLOCK_CODE = 'app/DateSelection/SUBMIT_BLOCK_CODE';
const RESET_BLOCK_CODE = 'app/DateSelection/RESET_BLOCK_CODE';
const SET_ACTIVE_BLOCK = 'app/DateSelection/SET_ACTIVE_BLOCK';
const SET_SELECTED_OFFERS = 'app/DateSelection/SET_SELECTED_OFFERS';
const SET_IS_ADMIN = 'app/DateSelection/SET_IS_ADMIN';
const SET_WAIT_LIST_BOOKING = 'app/DateSelection/SET_WAIT_LIST_BOOKING';
const SET_WAIT_LIST_BOOKING_REASON =
  'app/DateSelection/SET_WAIT_LIST_BOOKING_REASON';
const SET_WAIT_LIST_BOOKING_COMMENT =
  'app/DateSelection/SET_WAIT_LIST_BOOKING_COMMENT';
const SET_ADMIN_SELECTED_OFFERS = 'app/DateSelection/SET_ADMIN_SELECTED_OFFERS';
const SET_HOTEL_OVERRIDE_CODES = 'app/DateSelection/SET_HOTEL_OVERRIDE_CODES';
const SET_MAX_STAY_EXCEEDED = 'app/DateSelection/SET_MAX_STAY_EXCEEDED';

const monthStartMoment = moment();
const monthEndMoment = monthStartMoment.clone().endOf('month');

const monthStart = moment().isBetween(monthStartMoment, monthEndMoment, 'day')
  ? moment().format('YYYY-MM-DD')
  : monthStartMoment.format('YYYY-MM-DD');

const defaultProperty = getDefaultPropertyForTheme();
const monthEnd =
  defaultProperty === PROPERTY_WETUMPKA
    ? monthEndMoment.add(1, 'days').format('YYYY-MM-DD')
    : monthEndMoment.format('YYYY-MM-DD');

// The initial state of the App
const initialState = {
  startDate: null,
  endDate: null,
  roomType: {
    value: 'All',
    label: ROOM_TYPES.ALL
  },
  property: defaultProperty,
  numberOfGuests: {
    value: 1,
    label: 1
  },
  queryInput: {
    startDate: monthStart,
    endDate: monthEnd,
    propertyCode: PROPERTY_CODES[defaultProperty]
  },
  accountNumber: '',
  secondAccountNumber: '',
  accountPin: '',
  blockCode: null,
  submittedBlockCode: null,
  activeBlock: null,
  submittedAccount: {
    accountNumber: null,
    accountPin: null
  },
  secondSubmittedAccount: {
    accountNumber: null
  },
  selectedOffers: {},
  isAdmin: false,
  isWaitListBooking: false,
  waitListBookingReason: null,
  waitListBookingComment: '',
  adminSelectedOffers: null,
  hotelOverrideCodes: []
};

// Action Handlers
const ACTION_HANDLERS = {
  [SET_START_DATE]: (state, action) =>
    Object.assign({}, state, {
      startDate: action.payload.startDate
    }),
  [SET_END_DATE]: (state, action) =>
    Object.assign({}, state, {
      endDate: action.payload.endDate
    }),
  [SET_PROPERTY]: (state, action) =>
    Object.assign({}, state, {
      property: action.payload.property,
      queryInput: Object.assign({}, state.queryInput, {
        propertyCode: PROPERTY_CODES[action.payload.property],
        endDate:
          action.payload.property === PROPERTY_WETUMPKA
            ? moment(state.queryInput.endDate)
                .add(1, 'days')
                .format('YYYY-MM-DD')
            : state.queryInput.endDate
      })
    }),
  [SET_NUMBER_OF_GUESTS]: (state, action) =>
    Object.assign({}, state, {
      numberOfGuests: action.payload.numberOfGuests
    }),
  [SET_QUERY_INPUT]: (state, action) => {
    let queryInput = null;
    if (action.payload.currentMonthStart) {
      const monthStartMoment = moment(action.payload.currentMonthStart);
      const monthEndMoment = monthStartMoment.clone().endOf('month');

      const monthStart = moment().isBetween(
        monthStartMoment,
        monthEndMoment,
        'day',
        '[]'
      )
        ? moment().format('YYYY-MM-DD')
        : monthStartMoment.format('YYYY-MM-DD');

      const monthEnd =
        state.property === PROPERTY_WETUMPKA
          ? monthEndMoment.add(1, 'days').format('YYYY-MM-DD')
          : monthEndMoment.format('YYYY-MM-DD');

      queryInput = Object.assign({}, state.queryInput, {
        startDate: monthStart,
        endDate: monthEnd,
        propertyCode: PROPERTY_CODES[state.property]
      });
    }

    return Object.assign({}, state, {
      queryInput
    });
  },
  [SET_ACCOUNT_NUMBER]: (state, action) =>
    Object.assign({}, state, {
      accountNumber: action.payload.accountNumber
    }),

  [SET_SECOND_ACCOUNT_NUMBER]: (state, action) =>
    Object.assign({}, state, {
      secondAccountNumber: action.payload.secondAccountNumber
    }),

  [SET_ACCOUNT_PIN]: (state, action) =>
    Object.assign({}, state, {
      accountPin: action.payload.accountPin
    }),
  [SUBMIT_REWARDS_ACCOUNT]: state => {
    const { accountNumber, accountPin, secondAccountNumber } = state;

    let queryInput = Object.assign({}, state.queryInput, {});
    if (accountNumber) {
      queryInput.patronAccountNumber = accountNumber;
    }

    if (accountPin) {
      queryInput.pin = accountPin;
    }

    if (secondAccountNumber) {
      queryInput.secondPatronAccountNumber = secondAccountNumber
        ? secondAccountNumber
        : null;
    }

    return Object.assign({}, state, {
      submittedAccount: {
        accountNumber: state.accountNumber,
        accountPin: state.accountPin
      },
      secondSubmittedAccount: {
        accountNumber: secondAccountNumber ? secondAccountNumber : null
      },
      queryInput
    });
  },
  [SET_BLOCK_CODE]: (state, action) =>
    Object.assign({}, state, {
      blockCode: action.payload.blockCode
    }),
  [SUBMIT_BLOCK_CODE]: state =>
    Object.assign({}, state, {
      submittedBlockCode: state.blockCode,
      // Reset activeBlock if block code submitted is different from the active block.
      activeBlock:
        state.activeBlock && state.activeBlock.code === state.blockCode
          ? state.activeBlock
          : null,
      // Note that we also clear out the selected start and end dates here as well. This is to
      // force the user to reselect dates now that the block code rates are loaded.
      startDate: null,
      endDate: null
    }),
  [RESET_BLOCK_CODE]: state =>
    Object.assign({}, state, {
      blockCode: null,
      submittedBlockCode: null,
      activeBlock: null
    }),
  [SET_ACTIVE_BLOCK]: (state, action) =>
    Object.assign({}, state, {
      activeBlock: action.payload.block
    }),
  [SET_SELECTED_OFFERS]: (state, action) =>
    Object.assign({}, state, {
      selectedOffers: action.payload.offers
    }),
  [SET_IS_ADMIN]: (state, action) =>
    Object.assign({}, state, {
      isAdmin: action.payload.isAdmin
    }),
  [SET_WAIT_LIST_BOOKING]: (state, action) =>
    Object.assign({}, state, {
      isWaitListBooking: action.payload.isWaitListBooking
    }),
  [SET_WAIT_LIST_BOOKING_REASON]: (state, action) =>
    Object.assign({}, state, {
      waitListBookingReason: action.payload.waitListBookingReason
    }),
  [SET_WAIT_LIST_BOOKING_COMMENT]: (state, action) =>
    Object.assign({}, state, {
      waitListBookingComment: action.payload.waitListBookingComment
    }),
  [SET_ADMIN_SELECTED_OFFERS]: (state, action) =>
    Object.assign({}, state, {
      adminSelectedOffers: action.payload.adminSelectedOffers
    }),
  [SET_HOTEL_OVERRIDE_CODES]: (state, action) => {
    return Object.assign({}, state, {
      hotelOverrideCodes: action.payload.hotelOverrideCodes
    });
  },
  [SET_MAX_STAY_EXCEEDED]: (state, action) => {
    return Object.assign({}, state, {
      maxStayExceeded: action.payload.maxStayExceeded
    });
  }
};

// Action Creators
export function setStartDate(startDate) {
  return {
    type: SET_START_DATE,
    payload: { startDate }
  };
}

export function setEndDate(endDate) {
  return {
    type: SET_END_DATE,
    payload: { endDate }
  };
}

export function setProperty(property) {
  return {
    type: SET_PROPERTY,
    payload: { property }
  };
}

export function setNumberOfGuests(numberOfGuests) {
  return {
    type: SET_NUMBER_OF_GUESTS,
    payload: { numberOfGuests }
  };
}

export function setQueryInput(currentMonthStart) {
  return {
    type: SET_QUERY_INPUT,
    payload: { currentMonthStart }
  };
}

export function setAccountNumber(accountNumber) {
  return {
    type: SET_ACCOUNT_NUMBER,
    payload: { accountNumber }
  };
}

export function setSecondAccountNumber(secondAccountNumber) {
  return {
    type: SET_SECOND_ACCOUNT_NUMBER,
    payload: { secondAccountNumber }
  };
}

export function setAccountPin(accountPin) {
  return {
    type: SET_ACCOUNT_PIN,
    payload: { accountPin }
  };
}

export function submitRewardsAccount() {
  return {
    type: SUBMIT_REWARDS_ACCOUNT
  };
}

export function setBlockCode(blockCode) {
  return {
    type: SET_BLOCK_CODE,
    payload: { blockCode }
  };
}

export function submitBlockCode() {
  return {
    type: SUBMIT_BLOCK_CODE
  };
}

export function setActiveBlock(block) {
  return {
    type: SET_ACTIVE_BLOCK,
    payload: { block }
  };
}

export function resetBlockCode() {
  return {
    type: RESET_BLOCK_CODE
  };
}

export function setSelectedOffers(offers) {
  return {
    type: SET_SELECTED_OFFERS,
    payload: { offers }
  };
}

export function setWaitListBooking(isWaitListBooking) {
  return {
    type: SET_WAIT_LIST_BOOKING,
    payload: { isWaitListBooking }
  };
}

export function setWaitListBookingReason(waitListBookingReason) {
  return {
    type: SET_WAIT_LIST_BOOKING_REASON,
    payload: { waitListBookingReason }
  };
}

export function setWaitListBookingComment(waitListBookingComment) {
  return {
    type: SET_WAIT_LIST_BOOKING_COMMENT,
    payload: { waitListBookingComment }
  };
}

export function setIsAdmin(isAdmin) {
  return {
    type: SET_IS_ADMIN,
    payload: { isAdmin }
  };
}

export function setAdminSelectedOffers(adminSelectedOffers) {
  return {
    type: SET_ADMIN_SELECTED_OFFERS,
    payload: { adminSelectedOffers }
  };
}

export function setHotelOverrideCodes(hotelOverrideCodes) {
  return {
    type: SET_HOTEL_OVERRIDE_CODES,
    payload: { hotelOverrideCodes }
  };
}

export function setMaxStayExceeded(maxStayExceeded) {
  return {
    type: SET_MAX_STAY_EXCEEDED,
    payload: { maxStayExceeded }
  };
}

export function onStartDateChange(date) {
  return (dispatch, getState) => {
    const { queryInput, endDate } = getState().app.dateSelection;
    const momentStartDate = moment(date);
    const savedStartDate =
      typeof date !== 'undefined' &&
      momentStartDate.isSameOrAfter(moment().startOf('day'))
        ? momentStartDate.format('MM/DD/YYYY')
        : null;
    const momentEndDate = endDate && moment(endDate);
    const maxStayExceeded = isMaxStayExceeded(momentStartDate, momentEndDate);

    if (maxStayExceeded) {
      dispatch(setMaxStayExceeded(true));
      dispatch(setStartDate(null));
      dispatch(setEndDate(null));
    } else {
      dispatch(setStartDate(savedStartDate ? new Date(savedStartDate) : null));
    }

    // If new start date is in different month than the active calendar month, update the query input to load that data.
    if (
      savedStartDate &&
      momentStartDate.month() !== moment(queryInput.startDate).month()
    ) {
      dispatch(setQueryInput(momentStartDate.clone().startOf('month')));
    }
  };
}

export function onEndDateChange(date) {
  return (dispatch, getState) => {
    const { queryInput, startDate } = getState().app.dateSelection;
    const momentEndDate = moment(date);
    const savedEndDate =
      date && momentEndDate.isAfter(moment())
        ? momentEndDate.format('MM/DD/YYYY')
        : null;
    const momentStartDate = startDate && moment(startDate);
    const maxStayExceeded = isMaxStayExceeded(momentStartDate, momentEndDate);

    if (maxStayExceeded) {
      dispatch(setMaxStayExceeded(true));
      dispatch(setStartDate(null));
      dispatch(setEndDate(null));
    } else {
      dispatch(setEndDate(savedEndDate ? new Date(savedEndDate) : null));
    }

    // If new end date is in different month than the active calendar month, update the query input to load that data.
    if (
      savedEndDate &&
      momentEndDate.month() !== moment(queryInput.startDate).month()
    ) {
      dispatch(setQueryInput(momentEndDate.clone().startOf('month')));
    }
  };
}

// Reducer
export default function reducer(state = initialState, action) {
  const handler = ACTION_HANDLERS[action.type];

  return handler ? handler(state, action) : state;
}
